import type { OverridedMixpanel } from 'mixpanel-browser';

import { IS_SERVER } from '../ssr';

type MixpanelQueueItem = {
  [K in keyof OverridedMixpanel]: OverridedMixpanel[K] extends (...args: any[]) => any
    ? [K, Parameters<OverridedMixpanel[K]>]
    : never;
}[keyof OverridedMixpanel];

// If we run things in client before mixpanel has loaded, we queue them up
const queue: MixpanelQueueItem[] = [];

let mixpanel: OverridedMixpanel | null = null;

const KNOWN_MIXPANEL_FNS = [
  'add_group',
  'alias',
  'clear_opt_in_out_tracking',
  'disable',
  'get_config',
  'get_distinct_id',
  'get_group',
  'get_property',
  'has_opted_in_tracking',
  'has_opted_out_tracking',
  'identify',
  'init',
  'opt_in_tracking',
  'opt_out_tracking',
  'push',
  'register',
  'register_once',
  'remove_group',
  'reset',
  'set_config',
  'set_group',
  'time_event',
  'track',
  'track_forms',
  'track_links',
  'track_pageview',
  'track_with_groups',
  'unregister',
  'start_session_recording',
  'stop_session_recording',
  'get_session_recording_properties',
];

// Proxy Mixpanel so that we can still use it in SSR without actually calling anything
// This is a safeguard
export const MixpanelProxy = new Proxy<OverridedMixpanel>({} as OverridedMixpanel, {
  get(_target, prop: keyof OverridedMixpanel) {
    if (mixpanel) {
      return mixpanel[prop];
    } else if (KNOWN_MIXPANEL_FNS.includes(prop)) {
      return function (...args: unknown[]) {
        console.log(`MixpanelProxy.call: ${prop}, Arguments:`, args);

        if (!IS_SERVER) {
          queue.push([prop, args] as MixpanelQueueItem);
        }
      };
    } else {
      console.log(`MixpanelProxy.get: ${prop}`);
    }
  },
});

/** Quick helper fn */
export const mixpanelTrack: (typeof MixpanelProxy)['track'] = (...args) => {
  try {
    console.log('gonna attempt mixpanel track', args);
    MixpanelProxy?.track(...args);
  } catch (error) {
    console.warn(error);
  }
};

// Dynamically import mixpanel-browser
export function initMixpanel() {
  if (!IS_SERVER) {
    import('mixpanel-browser/src/loaders/loader-module-core') // without session recording, otherwise it's HUGE
      .then((module) => {
        mixpanel = module.default as OverridedMixpanel;

        mixpanel.init(import.meta.env.VITE_PUBLIC_ENV__MIXPANEL_TOKEN, {
          api_host: '/mix',
          track_pageview: false,
          disable_persistence: true,
        });

        // Process the queue
        if (queue?.length) {
          for (const [fn, args] of queue) {
            const cb = mixpanel[fn];

            if (typeof cb === 'function') {
              try {
                // biome-ignore lint/complexity/noBannedTypes: Function is ok here
                (cb as Function)(...(args as Parameters<typeof cb>));
              } catch (error) {
                console.error(error);
              }
            }
          }
          queue.length = 0;
        }
      })
      .catch((err) => {
        console.error('Failed to load mixpanel-browser:', err);
      });
  }
}
